/**
 * The tag names out of a label list, alphabetically — the `tags: string[]`
 * clients have always been handed, now that a tag is a `kind = 'tag'` label
 * rather than a row in a standalone tags table.
 *
 * This lives here, not in the query layer, because it is a pure projection over
 * labels a caller already holds. Anyone with an entity's labels (from
 * `getLabelsForFiles`) can derive its tags without a second read, and the query
 * layer and the API agree on what a tag list is by construction rather than by
 * two copies staying in step.
 */
export function toTagNames(labels) {
    return labels
        .filter((label) => label.kind === 'tag')
        .map((label) => label.name)
        .sort((a, b) => a.localeCompare(b));
}
/**
 * The one list an entity is in, or null. **"Project"** in UI copy.
 *
 * Singular by construction, not by convention: `task_labels_one_list` /
 * `file_labels_one_list` allow at most one `kind = 'list'` membership per
 * entity. That is the invariant a list-grouped board rests on — it is why the
 * buckets partition a page instead of overlapping.
 *
 * Lives here for the same reason as `toTagNames`: it is a pure projection over
 * labels the caller already holds, so nobody needs a second read to answer it.
 */
export function toListLabel(labels) {
    return labels.find((label) => label.kind === 'list') ?? null;
}
/**
 * The collections an entity is in — **"Collections"** in UI copy — by name.
 *
 * Many per entity, unlike a list, which is why a collection can only ever
 * *filter* a board while a list can scope it. Not to be confused with the
 * internal `projects` table, which is the Doing-Team ↔ ClickUp work order —
 * and note that "Project" in the UI is the **list**, not this.
 */
export function toCollectionLabels(labels) {
    return labels
        .filter((label) => label.kind === 'collection')
        .sort((a, b) => a.name.localeCompare(b.name));
}
/**
 * The lists a **task** can go in.
 *
 * A list is scoped to one `item_type`, so a file list is not somewhere a task
 * can be filed — `/v1/tasks?list_id=` 404s one. Every surface that offers lists
 * to a task therefore has to apply the same two-part test, and it lives here so
 * they cannot drift: a surface that forgets it renders an option or a section
 * the API will refuse, which reads as "this list is empty" rather than as the
 * wrong kind of container.
 */
export function toTaskLists(labels) {
    return labels.filter((label) => label.kind === 'list' && label.item_type === 'task');
}
//# sourceMappingURL=labels.js.map